import * as React from "react";
import { useForm, FormProvider } from "react-hook-form";
import { validatePassword } from "../utils/validation";
import { changePassword } from "./auth";
import { ActivationFailReasons } from "./types";
import { useLogoutMutation } from "../crud/auth/hooks";
import { useNavigate } from "react-router-dom";
import { Urls } from "../domain/urls";

import { LoadingIcon } from "../components/system/loading-icon";
import { H2, Paragraph } from "../components/ui/typography";
import { Section } from "../components/system/section";
import { Button } from "../components/ui/button";
import { InputHook } from "../components/system/input-hook";

type Inputs = {
  token: string;
  newPassword1: string;
  newPassword2: string;
};

type Props = {
  onSuccessCallBack: () => void;
};

export const ChangePassword = ({
  onSuccessCallBack,
}: Props): React.ReactElement => {
  const logoutMutation = useLogoutMutation();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const [isSuccess, setSuccess] = React.useState<boolean>(false);

  const [nonFieldError, setNonFieldError] = React.useState<string>();
  const methods = useForm<Inputs>({ mode: "onChange" });

  const sucessCloseModal = () => {
    setTimeout(() => onSuccessCallBack(), 2000);
  };

  // note that form fields are camelCase but API returns snake_case field errors
  // be careful not to confuse what is a Form error (camelCase) and
  // what is an API error (snake_case)
  const onSubmit = async ({ newPassword1, newPassword2 }: Inputs) => {
    setSubmitted(true);
    const result = await changePassword(newPassword1, newPassword2);
    if (result.type === "success") {
      sucessCloseModal();
      setSuccess(true);
      // backend logs you out on password change
      logoutMutation.mutate({});
      navigate(`/${Urls.Login}`);
    }
    if (result.type === "error") {
      setSubmitted(false);
      const responseErrs = result?.errors;
      // API returns snakecase field error
      if (responseErrs?.new_password1) {
        // form expects camel case errors set Here
        methods.setError("newPassword1", {
          type: "manual",
          // access the snake_case field error here
          message: responseErrs.new_password1.join(", "),
        });
      }
      if (responseErrs?.new_password2) {
        methods.setError("newPassword2", {
          type: "manual",
          message: responseErrs.new_password2.join(", "),
        });
      }
      if (responseErrs?.non_field_errors) {
        setNonFieldError(responseErrs.non_field_errors[0]);
      } else if (responseErrs?.unknownError) {
        setNonFieldError(responseErrs.unknownError);
      }
    }
  };
  if (isSuccess === true) {
    return (
      <div>
        <H2>Success</H2>
        <Paragraph>You have successfully changed your password.</Paragraph>
      </div>
    );
  }
  if (nonFieldError) {
    return (
      <div>
        <H2>Reset Email</H2>
        <Paragraph>{nonFieldError}</Paragraph>
      </div>
    );
  }

  return (
    <Section title="Change Password">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-2 mt-2 mb-6">
            <InputHook
              name="newPassword1"
              placeholder="New password"
              type="password"
              required
              rules={{
                required: "New password is required",
                validate: (value: string) =>
                  validatePassword(value) === true ||
                  ActivationFailReasons.PasswordRequirements,
              }}
            />
            <InputHook
              name="newPassword2"
              placeholder="Confirm new password"
              type="password"
              required
              rules={{
                required: "Matching password is required",
                validate: (value: string) =>
                  value === methods.getValues("newPassword1") ||
                  "Passwords must match",
              }}
            />
          </div>
          <Button type="submit" disabled={submitted} className="w-full">
            {submitted ? <LoadingIcon /> : "Submit"}
          </Button>
        </form>
      </FormProvider>
    </Section>
  );
};
